import { loadBundleSpec } from '@framebridge/toolbox/BundleCartHelpers';
import FrameSpec from '@framebridge/toolbox/FrameSpec';
import {
  eligibleUpsellProducts,
  fittingVariants,
  frameAllowsUpsells,
  isChildLineOf,
  upsellPriceCents,
  upsellVariant,
} from '@framebridge/toolbox/upsellEligibility';

const joineryHost = (SDG?.Data?.joineryHost || '').replace(/\/$/, '');
const TABLETOP_TAG = 'Tabletop Frames';
const productTagsRequests = new Map();
const bundleSpecRequests = new Map();
let productsRequest = null;
let addOnProductsRequest = null;

function useCartUpsellProducts() {
  const fetchProducts = () => {
    if (!productsRequest) {
      productsRequest = fetch(`${joineryHost}/api/v1/shopify_products_by_tag/cart-upsell`)
        .then((res) => {
          if (!res.ok) throw new Error(`cart upsell products request failed with ${res.status}`);

          return res.json();
        })
        .then((data) => (Array.isArray(data) ? data.map((entry) => entry.product) : []))
        .catch((err) => {
          productsRequest = null;
          console.error('cart upsell products error: ', err);
          return [];
        });
    }

    return productsRequest;
  };

  const fetchAddOnProducts = () => {
    if (!addOnProductsRequest) {
      addOnProductsRequest = fetch(`${joineryHost}/api/v1/shopify_products_by_tag/frame-review-addition`)
        .then((res) => {
          if (!res.ok) throw new Error(`frame review addition products request failed with ${res.status}`);

          return res.json();
        })
        .then((data) => (Array.isArray(data) ? data.map((entry) => entry.product) : []))
        .catch((err) => {
          addOnProductsRequest = null;
          console.error('frame review addition products error: ', err);
          return [];
        });
    }

    return addOnProductsRequest;
  };

  const productTags = (handle) => {
    if (!productTagsRequests.has(handle)) {
      productTagsRequests.set(handle, fetch(`/products/${handle}.js`)
        .then((res) => {
          if (!res.ok) throw new Error(`product tags request failed with ${res.status}`);

          return res.json();
        })
        .then((product) => product?.tags || [])
        .catch((err) => {
          // unknown tags could hide a tabletop, so a failure offers nothing and is retried later
          productTagsRequests.delete(handle);
          throw err;
        }));
    }

    return productTagsRequests.get(handle);
  };

  const parseFinalFrameSizeProperty = (value) => {
    const match = /([\d.]+)"\s*x\s*([\d.]+)"/.exec(value || '');

    return match ? { width: parseFloat(match[1]), height: parseFloat(match[2]) } : null;
  };

  const lineBundleSpec = (item) => {
    const { _bundle_spec_number: bundleSpecNumber, _bundle_spec_authorization_token: token } = item.properties || {};

    if (!bundleSpecRequests.has(bundleSpecNumber)) {
      bundleSpecRequests.set(bundleSpecNumber, loadBundleSpec(joineryHost, bundleSpecNumber, token)
        .catch((err) => {
          bundleSpecRequests.delete(bundleSpecNumber);
          throw err;
        }));
    }

    return bundleSpecRequests.get(bundleSpecNumber);
  };

  // A bundle's upsell links to its frame like the veneer flow does, so duplicating the bundle keeps it.
  const lineParentDesign = async (item) => {
    const { _frame_spec_number: frameSpecNumber, _bundle_spec_number: bundleSpecNumber } = item?.properties || {};

    if (frameSpecNumber || !bundleSpecNumber) {
      return frameSpecNumber || null;
    }

    try {
      const bundleSpec = await lineBundleSpec(item);

      return bundleSpec?.components?.[0]?.frameSpec?.number || bundleSpecNumber;
    } catch (err) {
      console.error('cart upsell bundle spec error: ', err);

      return bundleSpecNumber;
    }
  };

  const bundleFinalFrameSize = async (item) => {
    const bundleSpec = await lineBundleSpec(item);

    if (bundleSpec?.components?.length !== 1) {
      return null;
    }

    const component = bundleSpec.components[0];
    if (component.frameSpec?.finalFrameSize?.width) {
      return component.frameSpec.finalFrameSize;
    }

    const frameSpec = await FrameSpec.build({ frameSpecData: component.frameSpec, joineryHost, usedFullMats: [] });

    return frameSpec.calculateFinalFrameSize();
  };

  const lineFinalFrameSize = async (item, frameSpec) => {
    if (frameSpec?.finalFrameSize?.width) {
      return frameSpec.finalFrameSize;
    }

    const fromProperty = parseFinalFrameSizeProperty(item.properties?.['Final Frame Size']);
    if (fromProperty) {
      return fromProperty;
    }

    if (frameSpec) {
      return frameSpec.calculateFinalFrameSize();
    }

    if (item.properties?._bundle_spec_number) {
      return bundleFinalFrameSize(item);
    }

    return null;
  };

  const lineFrame = async (item, frameSpec) => ({
    productType: item.product_type,
    isTableTop: frameSpec ? frameSpec.isTableTopFrame() : (await productTags(item.handle)).includes(TABLETOP_TAG),
    isSingleFrame: true,
    isDigital: frameSpec ? frameSpec.isDigital() : !(item.variant_options || []).includes('Physical'),
  });

  const toCartUpsellItem = (product) => {
    const image = (product.media || []).find((media) => media.mediaContentType === 'IMAGE');

    return {
      available: product.status === 'ACTIVE' && Boolean(upsellVariant(product)?.purchasable),
      id: product.shopifyProductId,
      title: product.title,
      price: upsellPriceCents(product),
      featured_image: image?.image?.url || null,
      variants: [{ id: upsellVariant(product)?.shopifyVariantId }],
    };
  };

  const giftBoxVariant = (product, frameSpec, finalFrameSize) => {
    if (!product || !finalFrameSize || frameSpec.isTableTopFrame()) {
      return null;
    }

    const defaultSku = frameSpec.product?.defaultGiftBoxSku;

    return product.variants.find((variant) => variant.joinerySku && variant.joinerySku === defaultSku)
      || fittingVariants(product, finalFrameSize)[0]
      || null;
  };

  const activeGiftBoxProduct = async () => {
    const product = (await fetchAddOnProducts()).find((addOn) => addOn.handle === 'gift-box');

    return product && product.status === 'ACTIVE' ? product : null;
  };

  const toGiftBoxItem = (product, variant, locked) => {
    const image = (product.media || []).find((media) => media.mediaContentType === 'IMAGE');
    const price = parseFloat(variant.price);

    return {
      available: true,
      id: product.shopifyProductId,
      title: product.title,
      price: locked || Number.isNaN(price) ? 0 : Math.round(price * 100),
      featured_image: variant.image?.url || image?.image?.url || null,
      variants: [{ id: variant.shopifyVariantId }],
      joinerySku: variant.joinerySku,
      kind: 'gift-box',
      locked,
      priceLabel: locked ? 'Included' : null,
    };
  };

  const lineBundleGiftBoxItem = async (item) => {
    const joinerySku = item.properties?._gift_box_sku;

    if (!joinerySku) {
      return null;
    }

    try {
      const product = await activeGiftBoxProduct();
      const variant = (product?.variants || []).find((giftBox) => giftBox.joinerySku === joinerySku);

      return variant ? toGiftBoxItem(product, variant, true) : null;
    } catch (err) {
      console.error('cart bundle gift box error: ', err);

      return null;
    }
  };

  const lineGiftBoxItem = async (item, frameSpec = null) => {
    if (!frameSpec?.product || !frameAllowsUpsells({ productType: item.product_type })) {
      return null;
    }

    try {
      const frame = await lineFrame(item, frameSpec);
      if (!frameAllowsUpsells(frame)) {
        return null;
      }

      const locked = Boolean(frameSpec.isAutoGiftBox());
      if (!locked && (
        frameSpec.isCuratedDesignFrame()
        || !frame.isDigital
        || !(await frameSpec.giftBoxSupportsMouldingHeight())
      )) {
        return null;
      }

      const product = await activeGiftBoxProduct();
      if (!product) {
        return null;
      }

      const variant = giftBoxVariant(product, frameSpec, await lineFinalFrameSize(item, frameSpec));

      if (!variant || (!locked && variant.availableForSale === false)) {
        return null;
      }

      return toGiftBoxItem(product, variant, locked);
    } catch (err) {
      console.error('cart gift box error: ', err);

      return null;
    }
  };

  const lineUpsellItems = async (item, frameSpec = null) => {
    if (!frameAllowsUpsells({ productType: item.product_type })) {
      return [];
    }

    const products = await fetchProducts();
    if (!products.length) {
      return [];
    }

    try {
      const frame = await lineFrame(item, frameSpec);
      if (!frameAllowsUpsells(frame)) {
        return [];
      }

      const finalFrameSize = await lineFinalFrameSize(item, frameSpec);

      return eligibleUpsellProducts({ products, finalFrameSize, ...frame }).map(toCartUpsellItem);
    } catch (err) {
      console.error('cart upsell size error: ', err);

      return [];
    }
  };

  // Upsell lines already on the frame stay removable even when their product is no longer offered.
  const childUpsellItems = (item, cartLines = [], offeredItems = []) => {
    const offeredVariantIds = offeredItems.map((offered) => `${offered.variants?.[0]?.id}`);

    return cartLines
      .filter((line) => line.properties?._upsell_item === 'true' && isChildLineOf(line, item?.properties))
      .filter((line) => !offeredVariantIds.includes(`${line.variant_id}`))
      .map((line) => ({
        available: true,
        id: line.product_id,
        title: line.product_title || line.title,
        price: line.price,
        featured_image: line.featured_image?.url || line.image || null,
        variants: [{ id: line.variant_id }],
      }));
  };

  return {
    childUpsellItems,
    fetchProducts,
    lineBundleGiftBoxItem,
    lineGiftBoxItem,
    lineParentDesign,
    lineUpsellItems,
  };
}

export default useCartUpsellProducts;
